import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Radar, Plus, Trash2, Globe, ChevronRight, X, Search } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { apiPost, apiDelete } from '../lib/auth'
import { ScoreGauge } from '../components/ScoreGauge'
import { TopBar } from '../components/layout/TopBar'
import { AgencySelect } from '../features/partnerAgencies/AgencySelect'
import { listAgencies } from '../features/partnerAgencies/partnerAgenciesApi'
import type { PartnerAgency } from '../types/agency'

interface Project {
  id: number
  name: string
  domain: string
  gsc_property: string | null
  last_score: number | null
  created_at: string
  agency: { id: number; name: string } | null
}

export default function Projects() {
  const navigate = useNavigate()
  const { data: projects, loading, error, refetch } = useApi<Project[]>('/api/clients')

  const [showForm, setShowForm] = useState(false)
  const [formName, setFormName] = useState('')
  const [formDomain, setFormDomain] = useState('')
  const [formGsc, setFormGsc] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [agencies, setAgencies] = useState<PartnerAgency[]>([])
  const [agencyFilter, setAgencyFilter] = useState<number | 'all' | 'none'>('all')
  const [formAgencyId, setFormAgencyId] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    listAgencies().then((data) => { if (alive) setAgencies(Array.isArray(data) ? data : []) }).catch(() => { if (alive) setAgencies([]) })
    return () => { alive = false }
  }, [])

  if (error === 'Unauthorized' || error === '401') {
    navigate('/login')
    return null
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formName.trim() || !formDomain.trim()) {
      setFormError('Nom et domaine sont requis.')
      return
    }
    setFormLoading(true)
    setFormError(null)
    const { data, error: err } = await apiPost<{ id: number }>('/api/clients', {
      name: formName.trim(),
      domain: formDomain.trim(),
      gsc_property: formGsc.trim() || null,
      agencyId: formAgencyId,
    })
    setFormLoading(false)
    if (err) { setFormError(err); return }
    setFormName(''); setFormDomain(''); setFormGsc(''); setFormAgencyId(null); setShowForm(false)
    refetch()
    if (data?.id) navigate(`/p/${data.id}`)
  }

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.preventDefault(); e.stopPropagation()
    if (!confirm('Supprimer ce projet ?')) return
    setDeletingId(id)
    await apiDelete(`/api/clients/${id}`)
    setDeletingId(null)
    refetch()
  }

  const projectList = Array.isArray(projects) ? projects : []
  const filtered = projectList
    .filter(p => agencyFilter === 'all' ? true : agencyFilter === 'none' ? p.agency == null : p.agency?.id === agencyFilter)
    .filter(p =>
      !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.domain.toLowerCase().includes(search.toLowerCase())
    )

  return (
    <div className="min-h-screen bg-light-body text-[#1B2559]">
      <TopBar />

      <main className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1B2559]">Projets</h2>
            <p className="text-[#8B8FA7] text-sm mt-0.5">
              {projectList.length > 0 ? `${projectList.length} projet${projectList.length > 1 ? 's' : ''}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {projectList.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-9 pr-4 py-2 bg-white border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary transition-colors w-56"
                />
              </div>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" strokeWidth={1.25} />
              Nouveau projet
            </button>
          </div>
        </div>

        {agencies.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button
              onClick={() => setAgencyFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${agencyFilter === 'all' ? 'bg-orion-primary text-white' : 'bg-white border border-[#E2E4ED] text-[#555A73] hover:text-[#1B2559]'}`}
            >
              Tous
            </button>
            {agencies.map(a => (
              <button
                key={a.id}
                onClick={() => setAgencyFilter(a.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${agencyFilter === a.id ? 'bg-orion-primary text-white' : 'bg-white border border-[#E2E4ED] text-[#555A73] hover:text-[#1B2559]'}`}
              >
                {a.name}
              </button>
            ))}
            <button
              onClick={() => setAgencyFilter('none')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${agencyFilter === 'none' ? 'bg-orion-primary text-white' : 'bg-white border border-[#E2E4ED] text-[#555A73] hover:text-[#1B2559]'}`}
            >
              Non classé
            </button>
          </div>
        )}

        {showForm && (
          <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-[#1B2559]">Nouveau projet</h3>
              <button onClick={() => { setShowForm(false); setFormError(null) }} className="text-[#555A73] hover:text-[#1B2559] transition-colors">
                <X className="w-4 h-4" strokeWidth={1.25} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-[#8B8FA7] mb-1.5 block">Nom du projet *</label>
                  <input type="text" value={formName} onChange={e => setFormName(e.target.value)} placeholder="Ex: Elorion" className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-[#8B8FA7] mb-1.5 block">Domaine *</label>
                  <input type="text" value={formDomain} onChange={e => setFormDomain(e.target.value)} placeholder="Ex: elorion.fr" className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-[#8B8FA7] mb-1.5 block">Propriete GSC <span className="text-[#555A73]">(optionnel)</span></label>
                  <input type="text" value={formGsc} onChange={e => setFormGsc(e.target.value)} placeholder="sc-domain:elorion.fr" className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-[#8B8FA7] mb-1.5 block">Agence partenaire</label>
                  <AgencySelect
                    value={formAgencyId}
                    onChange={setFormAgencyId}
                    agencies={agencies}
                    ariaLabel="Agence partenaire"
                    className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] focus:outline-none focus:border-orion-primary transition-colors"
                  />
                </div>
              </div>
              {formError && <p className="text-score-critical text-sm">{formError}</p>}
              <div className="flex items-center gap-3">
                <button type="submit" disabled={formLoading} className="px-5 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50">
                  {formLoading ? 'Creation...' : 'Creer le projet'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setFormError(null) }} className="px-4 py-2.5 text-sm text-[#8B8FA7] hover:text-[#1B2559] transition-colors">Annuler</button>
              </div>
            </form>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && !error && projectList.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-5">
              <Radar className="w-9 h-9 text-[#555A73]" strokeWidth={1.25} />
            </div>
            <p className="text-xl font-bold text-[#1B2559] mb-2">Bienvenue sur Orion</p>
            <p className="text-[#8B8FA7] text-sm mb-6 max-w-md">Creez votre premier projet pour commencer a auditer votre site web.</p>
            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" strokeWidth={1.25} />
              Creer mon premier projet
            </button>
          </div>
        )}

        {!loading && filtered && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map(project => (
              <Link
                key={project.id}
                to={`/p/${project.id}`}
                className="group flex items-center justify-between gap-3 bg-white border border-[#E2E4ED] rounded-2xl p-3 hover:border-[#C8CBD8] hover:bg-[#F4F5FA] transition-all"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-white border border-[#E2E4ED] flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${project.domain}&sz=64`}
                      alt=""
                      className="w-5 h-5"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1B2559] group-hover:text-orion-primary-text transition-colors truncate">{project.name}</p>
                    <p className="text-xs text-[#555A73] truncate">{project.domain}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={e => handleDelete(e, project.id)}
                    disabled={deletingId === project.id}
                    className="p-1.5 text-[#555A73] hover:text-score-critical hover:bg-score-critical/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} />
                  </button>
                  <ChevronRight className="w-4 h-4 text-[#555A73] group-hover:text-[#8B8FA7] transition-colors" strokeWidth={1.25} />
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
            Erreur : {error}
          </div>
        )}
      </main>
    </div>
  )
}
